import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useAuthMe, useCustomers } from '../../hooks/queries';
import { isSupportPersona } from '../../utils/support-user';
import {
  buildCustomerDetailPath,
  getCustomerEmailSearchParam,
  getCustomerOrderCount,
  getCustomerOrderPreview,
  isCustomerListEnrichmentInSync,
  isCustomerOrdersEnriched,
} from '../../utils/customer-navigation';
import {
  buildOrderLocationState,
  customerDetailReturn,
} from '../../utils/order-navigation';
import { formatBigCommerceCalendarDate } from '../../utils/format-bigcommerce-date';

const EMPTY_CUSTOMERS: Record<string, unknown>[] = [];

export function CustomerList() {
  const { environment } = useEnvironment();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { data: authMeResponse } = useAuthMe();
  const isSupport = isSupportPersona(authMeResponse?.data);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [email, setEmail] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [expandedCustomerId, setExpandedCustomerId] = useState<number | null>(null);
  const [customersWithOrders, setCustomersWithOrders] = useState<Record<string, unknown>[]>([]);
  const [customerGroupsMap, setCustomerGroupsMap] = useState<Record<number, string>>({});
  const [sortState, setSortState] = useState<{
    column: 'name' | 'email' | 'date' | 'group' | null;
    direction: 'asc' | 'desc';
  }>({ column: null, direction: 'desc' });

  const searchFilters = isSearching ? { email: email.trim() || undefined } : undefined;

  const shouldFetch = isSupport ? isSearching && !!email.trim() : true;

  const { data: response, isLoading: loading, error: queryError, isFetching } = useCustomers(
    page,
    limit,
    searchFilters,
    { enabled: shouldFetch },
  );
  const customers = Array.isArray(response?.data) ? response.data : EMPTY_CUSTOMERS;
  const requiresEmailSearch = isSupport && response?.meta?.requiresEmailSearch === true;
  const error = queryError
    ? String((queryError as any).message ?? queryError)
    : response?.success === false
    ? response.message
    : null;

  useEffect(() => {
    const restoredEmail =
      getCustomerEmailSearchParam(location.search) ||
      (location.state as { emailSearch?: string } | null)?.emailSearch;
    if (restoredEmail) {
      setEmail(restoredEmail);
      setIsSearching(true);
    }
  }, [location.search, location.state]);

  useEffect(() => {
    let cancelled = false;

    async function attachOrdersToSearchResults() {
      if (!isSearching || customers.length === 0) {
        setCustomersWithOrders(customers);
        return;
      }

      if (customers.every((customer) => isCustomerOrdersEnriched(customer))) {
        setCustomersWithOrders(customers);
        return;
      }

      // Drop stale enrichments immediately when the query result changes.
      setCustomersWithOrders(customers);

      const enriched = await Promise.all(
        customers.map(async (customer) => {
          if (isCustomerOrdersEnriched(customer)) {
            return customer;
          }

          try {
            const detail = await apiClient.getCustomerById(customer.id, email.trim());
            if (detail.success && detail.data) {
              const orders = Array.isArray(detail.data.orders) ? detail.data.orders : [];
              return {
                ...customer,
                orders_count: detail.data.orders_pagination?.total ?? orders.length,
                orders,
              };
            }
          } catch (err) {
            console.error(`Failed to load orders for customer ${customer.id}:`, err);
          }

          return customer;
        }),
      );

      if (!cancelled) {
        setCustomersWithOrders(enriched);
      }
    }

    attachOrdersToSearchResults();

    return () => {
      cancelled = true;
    };
  }, [customers, email, isSearching, isSupport]);

  useEffect(() => {
    // Fetch customer groups to create a mapping
    const fetchCustomerGroups = async () => {
      try {
        const response = await apiClient.getCustomerGroups(1, 250);
        if (response.success && response.data) {
          const groups = Array.isArray(response.data) ? response.data : [];
          const groupsMap: Record<number, string> = {};
          groups.forEach((group: any) => {
            if (group.id) {
              groupsMap[group.id] = group.name || `Group ${group.id}`;
            }
          });
          setCustomerGroupsMap(groupsMap);
        }
      } catch (err) {
        console.error('Failed to fetch customer groups:', err);
      }
    };

    fetchCustomerGroups();
  }, [environment]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setIsSearching(true);
      setPage(1);
      setExpandedCustomerId(null);
      setCustomersWithOrders([]);
      setSearchParams({ email: email.trim() });
    } else {
      handleClearSearch();
    }
  };

  const handleClearSearch = () => {
    setEmail('');
    setIsSearching(false);
    setPage(1);
    setExpandedCustomerId(null);
    setCustomersWithOrders([]);
    setSearchParams({});
  };

  // CRITICAL: getComparator MUST be defined before sortedCustomers useMemo
  // due to JavaScript temporal dead zone - const functions cannot be accessed before declaration
  const getComparator = (
    column: 'name' | 'email' | 'date' | 'group',
    direction: 'asc' | 'desc'
  ) => {
    switch (column) {
      case 'name':
        return (a: any, b: any) => {
          const aName = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
          const bName = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
          const comparison = aName.localeCompare(bName);
          return direction === 'asc' ? comparison : -comparison;
        };

      case 'email':
        return (a: any, b: any) => {
          const aVal = (a.email || '').toLowerCase();
          const bVal = (b.email || '').toLowerCase();
          const comparison = aVal.localeCompare(bVal);
          return direction === 'asc' ? comparison : -comparison;
        };

      case 'date':
        return (a: any, b: any) => {
          const aDate = a.date_created ? new Date(a.date_created) : new Date(0);
          const bDate = b.date_created ? new Date(b.date_created) : new Date(0);
          return direction === 'asc' ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
        };

      case 'group':
        return (a: any, b: any) => {
          const aGroupName = a.customer_group_name || customerGroupsMap[a.customer_group_id] || '';
          const bGroupName = b.customer_group_name || customerGroupsMap[b.customer_group_id] || '';
          const aVal = aGroupName.toLowerCase();
          const bVal = bGroupName.toLowerCase();
          const comparison = aVal.localeCompare(bVal);
          return direction === 'asc' ? comparison : -comparison;
        };

      default:
        return () => 0;
    }
  };

  const handleSort = (column: 'name' | 'email' | 'date' | 'group') => {
    if (loading) {
      return; // Prevent sorting during loading (per FR-011)
    }

    setSortState(prev => {
      if (prev.column === column) {
        // Toggle direction
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      } else {
        // New column, start with ascending
        return { column, direction: 'asc' };
      }
    });
  };

  const getSortIndicator = (column: 'name' | 'email' | 'date' | 'group') => {
    if (sortState.column !== column) {
      return null;
    }
    return sortState.direction === 'asc' ? '↑' : '↓';
  };

  // Sort customers based on sortState
  // NOTE: getComparator must be defined above this useMemo due to temporal dead zone
  const sortedCustomers = useMemo(() => {
    const source = isCustomerListEnrichmentInSync(customers, customersWithOrders)
      ? customersWithOrders
      : customers;
    if (!sortState.column) {
      // Default: maintain current server-side sort (date_created descending)
      return [...source];
    }

    const comparator = getComparator(sortState.column, sortState.direction);
    return [...source].sort(comparator);
  }, [customers, customersWithOrders, sortState, customerGroupsMap]);

  const getCustomerGroupLabel = (customer: any): string | null => {
    if (typeof customer.customer_group_name === 'string' && customer.customer_group_name.trim()) {
      return customer.customer_group_name;
    }
    const groupId = customer.customer_group_id;
    if (typeof groupId !== 'number' || groupId <= 0) {
      return null;
    }
    return customerGroupsMap[groupId] || `Group ${groupId}`;
  };

  const formatOrderDate = (dateString?: string) => formatBigCommerceCalendarDate(dateString);

  const toggleExpanded = (customerId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedCustomerId((current) => (current === customerId ? null : customerId));
  };

  const openCustomerDetail = (customerId: number) => {
    const emailContext = isSupport && isSearching ? email.trim() : undefined;
    navigate(buildCustomerDetailPath(customerId, emailContext));
  };

  const getCustomerName = (customer: any) => {
    if (customer.first_name || customer.last_name) {
      return `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
    }
    return 'N/A';
  };

  if (loading || (isSupport && isFetching && shouldFetch)) {
    return <div className="page-container"><div className="loading-state"><div className="spinner" />Loading customers…</div></div>;
  }

  if (error) {
    return <div className="page-container"><div className="error-banner">⚠ {error}</div></div>;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Customers</h1>
          <p className="page-subtitle">
            {isSupport
              ? isSearching
                ? `${sortedCustomers.length} result${sortedCustomers.length !== 1 ? 's' : ''} found`
                : 'Search by exact email to look up a customer'
              : isSearching
              ? `${sortedCustomers.length} result${sortedCustomers.length !== 1 ? 's' : ''} found`
              : `${sortedCustomers.length} customer${sortedCustomers.length !== 1 ? 's' : ''} · Page ${page}`}
          </p>
        </div>
        <div className="page-actions">
          {!isSupport && (
          <div className="pagination">
            <button className="btn btn-secondary btn-sm" onClick={() => setPage(page - 1)} disabled={page === 1}>← Prev</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setPage(page + 1)} disabled={customers.length < limit}>Next →</button>
          </div>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="card card-padded" style={{ marginBottom: '1.5rem' }}>
        <form onSubmit={handleSearch}>
          <div style={{ marginBottom: '1rem' }}>
            <div>
              <label className="info-label" style={{ display: 'block', marginBottom: '0.35rem' }}>
                Email{' '}
                <span
                  style={{
                    fontWeight: 400,
                    textTransform: 'none',
                    letterSpacing: 0,
                    fontSize: '0.75rem',
                    color: 'var(--text-muted)',
                  }}
                >
                  (exact match)
                </span>
              </label>
              <input
                className="form-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="customer@example.com"
                title="Email search requires exact match"
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>Search</button>
            {isSearching && <button type="button" className="btn btn-secondary btn-sm" onClick={handleClearSearch} disabled={loading}>Clear</button>}
          </div>
        </form>
      </div>

      {requiresEmailSearch || (isSupport && !isSearching) ? (
        <div className="table-wrap">
          <div className="empty-state">
            <div className="empty-state-icon">🔍</div>
            <p className="empty-state-title">Email search required</p>
            <p className="empty-state-desc">Enter a customer&apos;s full email address to look up their record. Browsing the full customer directory is not available in Support mode.</p>
          </div>
        </div>
      ) : sortedCustomers.length === 0 ? (
        <div className="table-wrap">
          <div className="empty-state">
            <div className="empty-state-icon">👥</div>
            <p className="empty-state-title">No customers found</p>
            {isSearching && <p className="empty-state-desc">Try adjusting your search criteria.</p>}
          </div>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table" style={{ minWidth: 760 }}>
            <thead>
              <tr>
                <th style={{ width: 40 }} aria-label="Expand orders" />
                <th className="sortable" onClick={() => handleSort('name')}>Name <span className="sort-indicator">{getSortIndicator('name')}</span></th>
                <th className="sortable" onClick={() => handleSort('email')}>Email <span className="sort-indicator">{getSortIndicator('email')}</span></th>
                <th className="sortable" onClick={() => handleSort('group')}>Customer Group <span className="sort-indicator">{getSortIndicator('group')}</span></th>
                <th>Orders</th>
                <th className="sortable" onClick={() => handleSort('date')}>Joined <span className="sort-indicator">{getSortIndicator('date')}</span></th>
              </tr>
            </thead>
            <tbody>
              {sortedCustomers.map((customer) => {
                const orderCount = getCustomerOrderCount(customer);
                const orderPreview = getCustomerOrderPreview(customer);
                const isExpanded = expandedCustomerId === customer.id;
                const emailContext = isSupport && isSearching ? email.trim() : undefined;
                const orderReturnState = buildOrderLocationState(
                  customerDetailReturn(customer.id as number, emailContext),
                );

                return (
                  <React.Fragment key={customer.id}>
                    <tr className="clickable" onClick={() => openCustomerDetail(customer.id)}>
                      <td onClick={(event) => event.stopPropagation()}>
                        {orderCount > 0 ? (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? 'Collapse orders' : 'Expand orders'}
                            onClick={(event) => toggleExpanded(customer.id, event)}
                            style={{ minWidth: 28, padding: '0.1rem 0.4rem' }}
                          >
                            {isExpanded ? '−' : '+'}
                          </button>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                        )}
                      </td>
                      <td style={{ fontWeight: 500 }}>{getCustomerName(customer)}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{customer.email || '—'}</td>
                      <td>
                        {customer.customer_group_id
                          ? <span className="badge badge-info">{getCustomerGroupLabel(customer)}</span>
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td style={{ color: 'var(--text-secondary)' }}>{orderCount > 0 ? orderCount : '—'}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>
                        {formatBigCommerceCalendarDate(customer.date_created)}
                      </td>
                    </tr>
                    {isExpanded && orderPreview.length > 0 && (
                      <tr className="customer-orders-expand-row">
                        <td />
                        <td colSpan={3} style={{ background: 'var(--surface-muted, #f8f9fa)' }} />
                        <td
                          colSpan={1}
                          style={{
                            background: 'var(--surface-muted, #f8f9fa)',
                            padding: '0.75rem 1rem',
                            verticalAlign: 'top',
                          }}
                        >
                          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                            Past Orders
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                            {orderPreview.map((order) => (
                              <div key={order.id} style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', fontSize: '0.9rem' }}>
                                <Link
                                  to={`/orders/${order.id}`}
                                  state={orderReturnState}
                                  onClick={(event) => event.stopPropagation()}
                                  style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--primary, #2563eb)', textDecoration: 'none' }}
                                >
                                  Order #{order.id}
                                </Link>
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                  {[order.status || 'Unknown', formatOrderDate(order.date_created)].filter(Boolean).join(' · ')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td style={{ background: 'var(--surface-muted, #f8f9fa)' }} />
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

